import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../lib/api";
import { getApiErrorMessage } from "../../lib/apiErrors";
import { Sparkles, CheckCircle, Zap, AlertTriangle, Info } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganizationScope } from "../../contexts/OrganizationScopeContext";
import { Card, CardHeader, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { toast } from "sonner";
import en from "../../locales/en";
import cs from "../../locales/cs";
import { localizeReasonMessage, hasSailWarningReason, sailWarningTooltip } from "../../lib/provisionalReasons";
import {
  groupProvisionalReasons, hasArcProvisionalReason, isFigure6UndeterminedAirspace, isWarningGroup, provisionalGroupTitle,
  provisionalReasonGroup,
} from "../../lib/arcDisplay";

// Import panels
import { SoraSummaryPanel } from "./panels/SoraSummaryPanel";
import { PreFlightEquipmentChecklist } from "./panels/PreFlightEquipmentChecklist";
import { DocumentGenerationCard } from "./cards/DocumentGenerationCard";
import { DocumentReviewCard } from "./cards/DocumentReviewCard";
import { SubmissionCard } from "./cards/SubmissionCard";

// Import Sprint E4 components
import { FastTrackCard } from "./cards/FastTrackCard";
import { ExpiringSoonBanner } from "./ExpiringSoonBanner";
import { LUCVerificationModal } from "./modals/LUCVerificationModal";
import { DocumentPreviewModal } from "./modals/DocumentPreviewModal";

// SAIL color mapping
const getSailColor = (sail) => {
  const colors = {
    I: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700",
    II: "bg-lime-100 text-lime-700 border-lime-300 dark:bg-lime-900/40 dark:text-lime-300 dark:border-lime-700",
    III: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700",
    IV: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700",
    V: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700",
    VI: "bg-red-200 text-red-800 border-red-400 dark:bg-red-900/60 dark:text-red-200 dark:border-red-600",
    // Final iGRC exceeds Table 2/7's scope — Category C (Certified), not a Specific SAIL. Without
    // this branch OUT_OF_SCOPE fell through to the neutral gray default, indistinguishable from
    // "not yet assessed".
    OUT_OF_SCOPE: "bg-rose-200 text-rose-900 border-rose-400 dark:bg-rose-900/60 dark:text-rose-200 dark:border-rose-600",
  };
  return colors[sail] || "bg-gray-100 text-gray-700 border-gray-300";
};

const isOutOfScopeSail = (sail) => (sail || "").toUpperCase() === "OUT_OF_SCOPE";

export const CheckMateDashboard = ({
  missionId,
  mission,
  aircraft,
  optimizationData,
  language = "cs",
  onMissionUpdate,
  onRefreshOptimizations,
  onNavigateToProvisionalTarget,
  isProvisionalTargetActionable
}) => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const t = language === "cs" ? cs : en;
  
  // Shared state - Optimizations
  const [selectedOptimizations, setSelectedOptimizations] = useState([]);
  const [applying, setApplying] = useState(false);
  
  // Shared state - Document Generation
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [loadingKb, setLoadingKb] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(null);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [previousGenerations, setPreviousGenerations] = useState([]);
  const [showDocPreview, setShowDocPreview] = useState(false);
  
  // Shared state - Review
  const [reviewFile, setReviewFile] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewDocType, setReviewDocType] = useState("auto");
  const [detectedType, setDetectedType] = useState(null);
  const [useMissionContext, setUseMissionContext] = useState(true);
  
  // Sprint E4: Fast Track state
  const [showLucModal, setShowLucModal] = useState(false);
  const [selectedLuc, setSelectedLuc] = useState(null);
  const [applyingFastTrack, setApplyingFastTrack] = useState(false);
  
  // WAVE 2A: Flight mode state
  const [flightMode, setFlightMode] = useState(mission?.flight_mode || "VLOS");
  const [savingFlightMode, setSavingFlightMode] = useState(false);
  
  // Derived data
  const state = optimizationData?.current_state;
  const currentSail = state?.current_sail || "?";
  
  // Sprint E4: Compute fast track and standard mitigations
  const fastTrackOptions = useMemo(() => 
    optimizationData?.all_optimizations?.filter(o => o.type === "fast_track") || []
  , [optimizationData]);
  
  const standardMitigations = useMemo(() => 
    optimizationData?.all_optimizations?.filter(o => o.type !== "fast_track") || []
  , [optimizationData]);
  
  const isFastTrackApplied = !!optimizationData?.fast_track_applied;
  const fastTrackAppliedData = optimizationData?.fast_track_applied;
  
  // Sprint E4: Get expiring/expired warnings
  const expiringSoonWarnings = optimizationData?.expiring_soon_warnings || [];
  const expiredWarnings = optimizationData?.expired_authorization_warnings || [];
  
  // Get mission drone class for LUC verification
  const missionDroneClass = mission?.ua_class || state?.ua_class || null;
  
  // Fetch knowledge base on mount
  const fetchKnowledgeBase = useCallback(async () => {
    if (!scopedOrganizationId) return;
    setLoadingKb(true);
    try {
      const response = await api.get(`/compliance/knowledge/${scopedOrganizationId}`);
      setKnowledgeBase(response.data);
    } catch (err) {
      console.error("Failed to fetch knowledge base:", err);
    } finally {
      setLoadingKb(false);
    }
  }, [scopedOrganizationId]);
  
  // Fetch previous generations on mount
  const fetchPreviousGenerations = useCallback(async () => {
    if (!missionId) return;
    try {
      const response = await api.get(`/compliance/generations/by-mission/${missionId}`);
      setPreviousGenerations(response.data || []);
    } catch (err) {
      console.error("Failed to fetch previous generations:", err);
    }
  }, [missionId]);
  
  useEffect(() => {
    fetchKnowledgeBase();
    fetchPreviousGenerations();
  }, [fetchKnowledgeBase, fetchPreviousGenerations]);
  
  // WAVE 2A: Sync flight mode when mission changes
  useEffect(() => {
    if (mission?.flight_mode) {
      setFlightMode(mission.flight_mode);
    }
  }, [mission?.flight_mode]);
  
  // WAVE 2A: Handle flight mode change
  const handleFlightModeChange = useCallback(async (newMode) => {
    if (newMode === flightMode) return;
    
    setSavingFlightMode(true);
    try {
      await api.patch(`/missions/${missionId}`, { flight_mode: newMode });
      setFlightMode(newMode);
      
      toast.success(
        language === 'cs' 
          ? `Režim letu změněn na ${newMode}` 
          : `Flight mode changed to ${newMode}`
      );
      
      // Refresh SORA assessment (main panel + CheckMate optimizations together)
      await Promise.all([
        onRefreshOptimizations?.(),
        onMissionUpdate?.(),
      ]);
    } catch (err) {
      console.error("Failed to update flight mode:", err);
      toast.error(
        language === 'cs'
          ? 'Změna režimu letu selhala'
          : 'Failed to update flight mode'
      );
    } finally {
      setSavingFlightMode(false);
    }
  }, [flightMode, missionId, language, onRefreshOptimizations, onMissionUpdate]);

  // CheckMate SAIL refresh unification (2026-09): applyFastTrack is declared before
  // handleFastTrackSelect (which calls it) so handleFastTrackSelect can list it as a
  // real dependency — a memoized [] deps array here would freeze the closure to the
  // first render's applyFastTrack/onMissionUpdate, going stale as onMissionUpdate's
  // identity changes over time (it now points to MissionDetail.js's
  // refreshMissionSoraData, whose own deps include environmentType state).
  const applyFastTrack = useCallback(async (suggestion, userVerifiedScope) => {
    if (!suggestion?.details) return;

    setApplyingFastTrack(true);
    try {
      await api.post(
        `/sora/fast_track_select/${missionId}`,
        {
          authorization_id: suggestion.details.authorization_id,
          fast_track_subtype: suggestion.details.fast_track_subtype,
          user_verified_scope: userVerifiedScope,
        }
      );

      toast.success(t.checkmate.fastTrack.successToast);
      setShowLucModal(false);
      setSelectedLuc(null);

      // Refresh optimization data to show fast_track_applied, plus the main SAIL panel
      // for consistency (fast-track selection doesn't change GRC/ARC/SAIL inputs today,
      // but keeping the refresh pattern uniform avoids this silently drifting stale if
      // that ever changes).
      await Promise.all([
        onRefreshOptimizations?.(),
        onMissionUpdate?.(),
      ]);
    } catch (err) {
      toast.error(getApiErrorMessage(err, language, t.checkmate.fastTrack.errorToast));
    } finally {
      setApplyingFastTrack(false);
    }
  }, [missionId, onRefreshOptimizations, onMissionUpdate, t, language]);

  // Callback to refresh cards after applying optimizations
  const onOptimizationsApplied = useCallback(async () => {
    fetchPreviousGenerations();
    // Refresh optimization data + main SAIL panel together (e.g. max_altitude_m changes
    // aren't picked up by MissionDetail.js's own useEffect deps, so this explicit call
    // is what keeps the banner in sync).
    await Promise.all([
      onRefreshOptimizations?.(),
      onMissionUpdate?.(),
    ]);
  }, [fetchPreviousGenerations, onRefreshOptimizations, onMissionUpdate]);

  // Sprint E4: Fast Track handlers
  const handleFastTrackSelect = useCallback(async (suggestion) => {
    const subtype = suggestion?.details?.fast_track_subtype;

    // If LUC_SELF_AUTHORIZE, open verification modal
    if (subtype === "LUC_SELF_AUTHORIZE") {
      setSelectedLuc(suggestion);
      setShowLucModal(true);
      return;
    }

    // Otherwise, apply directly (no scope verification needed)
    await applyFastTrack(suggestion, false);
  }, [applyFastTrack]);
  
  const handleLucConfirm = useCallback(async () => {
    if (selectedLuc) {
      await applyFastTrack(selectedLuc, true);
    }
  }, [selectedLuc, applyFastTrack]);
  
  // Build applicable changes from selected optimizations (mirrors SoraAssistant_rollback_wizard.jsx)
  const getApplicableChanges = useCallback(() => {
    const changes = [];
    const state = optimizationData?.current_state;
    
    for (const opt of selectedOptimizations) {
      if (opt.type === "altitude" && opt.details?.new_altitude_m) {
        changes.push({
          field: "max_altitude_m",
          old_value: mission?.max_altitude_m || state?.max_altitude_m || 100,
          new_value: opt.details.new_altitude_m,
          label_key: "maxAltitude"
        });
      } else if (opt.type === "drone" && opt.details?.alternative_drone_id) {
        changes.push({
          field: "aircraft_id",
          old_value: mission?.aircraft_id,
          new_value: opt.details.alternative_drone_id,
          old_label: state?.drone_model,
          new_label: opt.details.alternative_drone_model,
          label_key: "aircraft"
        });
      }
    }
    
    return changes;
  }, [selectedOptimizations, mission, optimizationData]);
  
  // Apply selected optimizations via API
  const handleApplyOptimizations = useCallback(async () => {
    if (selectedOptimizations.length === 0) return;
    
    const changes = getApplicableChanges();
    
    // If no applicable changes (e.g., only equipment optimizations selected), show info
    if (changes.length === 0) {
      toast.info(
        language === 'cs' 
          ? 'Vybrané optimalizace vyžadují manuální změny (např. vybavení dronu)' 
          : 'Selected optimizations require manual changes (e.g., drone equipment)'
      );
      return;
    }
    
    setApplying(true);
    try {
      const updateData = {};
      const changeRecords = [];
      
      for (const change of changes) {
        updateData[change.field] = change.new_value;
        changeRecords.push({
          field: change.field,
          old_value: change.old_value,
          new_value: change.new_value,
          timestamp: new Date().toISOString()
        });
      }
      
      // Call PATCH endpoint with changes and metadata
      await api.patch(
        `/missions/${missionId}/sora-apply`,
        {
          updates: updateData,
          sora_assistant_changes: changeRecords
        }
      );
      
      toast.success(language === 'cs' ? 'Optimalizace aplikovány' : 'Optimizations applied successfully');
      setSelectedOptimizations([]);
      await onOptimizationsApplied();
      
    } catch (err) {
      console.error("Failed to apply optimizations:", err);
      toast.error(
        getApiErrorMessage(err, language, language === 'cs' ? 'Aplikování selhalo' : 'Failed to apply optimizations')
      );
      // Don't clear selection on error so user can retry
    } finally {
      setApplying(false);
    }
  }, [selectedOptimizations, getApplicableChanges, missionId, language, onOptimizationsApplied]);

  return (
    <Card className="border-[var(--card-border)] bg-[var(--card-bg)]" data-testid="checkmate-dashboard">
      {/* Header */}
      <CardHeader className="pb-4 border-b border-[var(--border-default)]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Left: Title + SAIL badge */}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
              <Sparkles className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">CheckMate</h2>
              <p className="text-xs text-[var(--text-muted)]">
                {language === 'cs' ? 'SORA 2.5 Compliance Engine' : 'SORA 2.5 Compliance Engine'}
              </p>
            </div>
            <Badge className={`${getSailColor(currentSail)} text-sm px-3 py-1 font-semibold border ml-2 gap-1`}>
              {isOutOfScopeSail(currentSail)
                ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA')
                : `SAIL ${currentSail}`}
              {(state?.grc_provisional || hasArcProvisionalReason(state?.provisional_reasons))
                && hasSailWarningReason(state?.provisional_reasons) && (
                // A `title` prop on lucide's <svg> becomes an SVG attribute, not a <title> child
                // element — needs a title-bearing wrapper for a real browser tooltip.
                <span title={sailWarningTooltip(state?.provisional_reasons, language)}>
                  <AlertTriangle className="h-3 w-3" />
                </span>
              )}
            </Badge>
          </div>
          
          {/* Right: Mission ID */}
          <div className="text-right">
            <span className="text-sm font-medium text-[var(--text-secondary)]">
              {mission?.mission_id || mission?.name || `MSN-${missionId?.slice(-6)}`}
            </span>
          </div>
        </div>
      </CardHeader>
      
      {/* Two-Panel Layout */}
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-[40%_60%] min-h-[500px]">
          {/* Left Panel - SORA Summary */}
          <div className="p-6 border-b lg:border-b-0 lg:border-r border-[var(--border-default)] bg-[var(--bg-secondary)] space-y-6">
            <SoraSummaryPanel
              optimizationData={optimizationData}
              selectedOptimizations={selectedOptimizations}
              setSelectedOptimizations={setSelectedOptimizations}
              applying={applying}
              onApplyOptimizations={handleApplyOptimizations}
              language={language}
              // WAVE 2A: Flight mode
              flightMode={flightMode}
              onFlightModeChange={handleFlightModeChange}
            />
            
            {/* Block E3b: Pre-flight Equipment Checklist */}
            <PreFlightEquipmentChecklist
              missionId={missionId}
              mission={mission}
              aircraft={aircraft}
              language={language}
              onEquipmentConfirmed={async () => {
                // Confirmed equipment feeds both engines (see SoraAssessmentInputBuilder) —
                // refresh CheckMate's own optimizationData and the main SAIL panel together.
                // Note: PreFlightEquipmentChecklist.jsx itself doesn't await this callback
                // (out of this file's edit scope), so its local "Confirming..." spinner can
                // end slightly before both refreshes finish — cosmetic only, no data loss.
                await Promise.all([
                  onRefreshOptimizations?.(),
                  onMissionUpdate?.(),
                ]);
              }}
            />
          </div>
          
          {/* Right Panel - Action Cards */}
          <div className="p-6 space-y-4">
            {/* Table 2 iGRC provisional/out-of-scope notice (plan §3.9 point 5) — CheckMate reads
                its own current_state, not the main panel's soraAssessment (see field comment on
                MissionCurrentState), so this is a separate notice from MissionDetail.js's banner. */}
            {/* The ARC reasons (undetermined airspace, ARC-a declaration) come in the same
                provisional_reasons but never set grc_provisional, so they get their own notice below. */}
            {(state?.grc_out_of_scope || state?.grc_provisional) && (() => {
              const allReasons = (state?.provisional_reasons || []).filter(r => provisionalReasonGroup(r.code) === 'grc');
              const isActionable = (target) => onNavigateToProvisionalTarget &&
                (isProvisionalTargetActionable ? isProvisionalTargetActionable(target) : !!target);
              const actionableReasons = allReasons.filter(r => isActionable(r.target));
              // target=null (manual_footprint, automatic_grc1_exemption) is a permanent property of
              // the estimation method, not missing data — shown as a neutral note instead of looking
              // like an unresolved problem with nothing to press (2026-09-16, live user testing).
              const infoReasons = allReasons.filter(r => !isActionable(r.target));
              // 2026-09-16 (follow-up): color/urgency follows whether there's anything to actually
              // press, not just grc_provisional — an all-info case (only manual_footprint/
              // automatic_grc1_exemption) stayed amber "action needed" before this fix, which was
              // itself reported as confusing.
              const isWarning = state?.grc_out_of_scope || actionableReasons.length > 0;
              return (
                <div
                  className={`p-3 rounded-lg border flex items-start gap-3 ${
                    state?.grc_out_of_scope
                      ? "bg-rose-50 dark:bg-rose-900/20 border-rose-300 dark:border-rose-800"
                      : isWarning
                      ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800"
                      : "bg-slate-50 dark:bg-slate-800/40 border-slate-300 dark:border-slate-700"
                  }`}
                  data-testid="checkmate-grc-provisional-notice"
                >
                  {isWarning
                    ? <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${state?.grc_out_of_scope ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400"}`} />
                    : <Info className="h-5 w-5 flex-shrink-0 text-[var(--text-muted)]" />}
                  <div className="space-y-1">
                    <p className={`text-sm font-semibold ${
                      state?.grc_out_of_scope
                        ? "text-rose-800 dark:text-rose-200"
                        : isWarning
                        ? "text-amber-800 dark:text-amber-200"
                        : "text-[var(--text-primary)]"
                    }`}>
                      {state?.grc_out_of_scope
                        ? (language === 'cs' ? 'Mimo rozsah SORA (Category C)' : 'Out of SORA scope (Category C)')
                        : t.arcDisplay.provisionalSimplifiedTitle}
                    </p>
                    {actionableReasons.map((reason, idx) => (
                      <div key={reason.code || idx} className="flex items-start justify-between gap-2">
                        {/* min-w-0 lets long text wrap within its own space instead of pushing the
                            button onto its own (left-aligned) flex-wrap line — 2026-09-16, live
                            user testing with 2 simultaneous reasons. */}
                        <p className="text-xs text-[var(--text-muted)] min-w-0 flex-1">{localizeReasonMessage(reason, language)}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs flex-shrink-0"
                          onClick={() => onNavigateToProvisionalTarget(reason.target)}
                        >
                          {language === 'cs' ? 'Doplnit' : 'Complete'}
                        </Button>
                      </div>
                    ))}
                    {infoReasons.map((reason, idx) => (
                      <p key={reason.code || idx} className="text-xs text-[var(--text-muted)]">{localizeReasonMessage(reason, language)}</p>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ARC notices: the undetermined airspace (worst-case ARC, fast track and approval blocked) and the
                operator's ARC-a declaration, one per group; none of them has anything to complete. */}
            {(() => {
              const arcGroups = groupProvisionalReasons(state?.provisional_reasons).filter(g => g.group !== 'grc');
              if (isFigure6UndeterminedAirspace(state) && !arcGroups.some(g => g.group === 'airspace')) {
                arcGroups.unshift({ group: 'airspace', reasons: [] });
              }
              return arcGroups.map(({ group, reasons }) => {
                const isWarning = isWarningGroup(group, reasons);
                return (
                  <div
                    key={group}
                    className={`p-3 rounded-lg border flex items-start gap-3 ${
                      isWarning
                        ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800"
                        : "bg-slate-50 dark:bg-slate-800/40 border-slate-300 dark:border-slate-700"
                    }`}
                    data-testid={`checkmate-arc-notice-${group}`}
                  >
                    {isWarning
                      ? <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                      : <Info className="h-5 w-5 flex-shrink-0 text-[var(--text-muted)]" />}
                    <div className="space-y-1">
                      <p className={`text-sm font-semibold ${isWarning ? "text-amber-800 dark:text-amber-200" : "text-[var(--text-primary)]"}`}>
                        {provisionalGroupTitle(group, reasons, t)}
                      </p>
                      {reasons.map((reason, idx) => (
                        <p key={reason.code || idx} className="text-xs text-[var(--text-muted)]">{localizeReasonMessage(reason, language)}</p>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}

            {/* Sprint E4: Expiring Soon Banner */}
            <ExpiringSoonBanner
              expiringSoonWarnings={expiringSoonWarnings}
              expiredWarnings={expiredWarnings}
              language={language}
            />
            
            {/* Sprint E4: Already Applied Badge */}
            {isFastTrackApplied && fastTrackAppliedData && (
              // 2026-09-16 review (Codex gpt-5.6-sol): a fast-track applied earlier can be stale —
              // the mission's risk assessment may have changed since (e.g. Table 2 inputs edited)
              // and now be out of SORA scope, where FastTrackSelectAsync would reject this same
              // selection today. A green "success" badge would misleadingly imply the mission is
              // still covered; show a warning instead when that's no longer true.
              state?.grc_out_of_scope ? (
                <div
                  className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-300 dark:border-rose-800 flex items-center gap-3"
                  data-testid="fast-track-applied-stale-warning"
                >
                  <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
                      {t.arcDisplay.fastTrackStaleTitle}
                    </p>
                    <p className="text-xs text-rose-600 dark:text-rose-400">
                      {language === 'cs'
                        ? 'Mise je nyní mimo rozsah SORA — žádná fast-track autorizace ji nemůže pokrýt, nový výběr by byl odmítnut.'
                        : "The mission is now outside SORA scope — no fast-track authorization can cover it, a new selection would be rejected."}
                    </p>
                  </div>
                </div>
              ) : isFigure6UndeterminedAirspace(state) ? (
                // The ARC cannot be determined now: fast_track_select would reject the same selection today.
                <div
                  className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 flex items-center gap-3"
                  data-testid="fast-track-applied-stale-arc-warning"
                >
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                      {t.arcDisplay.fastTrackStaleTitle}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      {t.arcDisplay.fastTrackStaleArc}
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3"
                  data-testid="fast-track-applied-badge"
                >
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                      {t.checkmate.fastTrack.alreadyApplied}
                    </p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      {t.checkmate.fastTrack.appliedBy
                        .replace("{email}", fastTrackAppliedData.selected_by_email || "unknown")
                        .replace("{date}", fastTrackAppliedData.selected_at
                          ? new Date(fastTrackAppliedData.selected_at).toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US")
                          : "unknown"
                        )
                      }
                    </p>
                  </div>
                </div>
              )
            )}
            
            {/* Sprint E4: Fast Track Section */}
            {fastTrackOptions.length > 0 && !isFastTrackApplied && (
              <section className="mb-6" data-testid="fast-track-section">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-5 w-5 text-purple-500" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider">
                    {t.checkmate.fastTrack.sectionTitle}
                  </h3>
                </div>
                <p className="text-xs text-[var(--text-muted)] mb-4">
                  {t.checkmate.fastTrack.sectionSubtitle}
                </p>
                <div className="space-y-3">
                  {fastTrackOptions.map((opt) => (
                    <FastTrackCard
                      key={opt.details?.authorization_id || opt.title}
                      suggestion={opt}
                      onSelect={handleFastTrackSelect}
                      language={language}
                      disabled={isFastTrackApplied || applyingFastTrack}
                    />
                  ))}
                </div>
              </section>
            )}
            
            {/* No fast tracks available message */}
            {fastTrackOptions.length === 0 && !isFastTrackApplied && (
              <div className="text-sm text-[var(--text-muted)] italic mb-4">
                {t.checkmate.fastTrack.noFastTracks}
              </div>
            )}
            
            {/* Compliance Actions Header */}
            <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-4">
              {language === 'cs' ? 'Compliance Akce' : 'Compliance Actions'}
            </h3>
            
            {/* Action Cards */}
            <div className="space-y-4">
              {/* Card 1: Document Generation */}
              <DocumentGenerationCard
                missionId={missionId}
                mission={mission}
                optimizationData={optimizationData}
                knowledgeBase={knowledgeBase}
                previousGenerations={previousGenerations}
                generatingDoc={generatingDoc}
                setGeneratingDoc={setGeneratingDoc}
                generatedDoc={generatedDoc}
                setGeneratedDoc={setGeneratedDoc}
                onShowPreview={() => setShowDocPreview(true)}
                onRefreshGenerations={fetchPreviousGenerations}
                user={user}
                language={language}
              />
              
              {/* Card 2: Document Review */}
              <DocumentReviewCard
                missionId={missionId}
                reviewFile={reviewFile}
                setReviewFile={setReviewFile}
                reviewing={reviewing}
                setReviewing={setReviewing}
                reviewResult={reviewResult}
                setReviewResult={setReviewResult}
                reviewDocType={reviewDocType}
                setReviewDocType={setReviewDocType}
                detectedType={detectedType}
                setDetectedType={setDetectedType}
                useMissionContext={useMissionContext}
                setUseMissionContext={setUseMissionContext}
                user={user}
                language={language}
              />
              
              {/* Card 3: CAA Submission */}
              <SubmissionCard
                missionId={missionId}
                previousGenerations={previousGenerations}
                reviewResult={reviewResult}
                language={language}
              />
            </div>
          </div>
        </div>
      </CardContent>
      
      {/* Sprint E4: LUC Verification Modal */}
      <LUCVerificationModal
        open={showLucModal}
        onClose={() => {
          setShowLucModal(false);
          setSelectedLuc(null);
        }}
        onConfirm={handleLucConfirm}
        luc={selectedLuc}
        mission={mission}
        missionSailLevel={currentSail}
        missionDroneClass={missionDroneClass}
        language={language}
      />

      {/* Document Preview Modal — was previously a dead click (showDocPreview was set but never rendered) */}
      <DocumentPreviewModal
        open={showDocPreview}
        onClose={() => setShowDocPreview(false)}
        document={generatedDoc}
        language={language}
      />
    </Card>
  );
};

export default CheckMateDashboard;
